<template>
  <div class="bb-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="bb-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Broadcast view from behind home plate: every point of the field
           (lines, bases, players, ball) goes through the same projection (p3). -->
      <svg class="bb-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern v-if="crowd" :id="uid + 'crowd'" patternUnits="userSpaceOnUse" width="90" height="45">
            <image :href="crowd" x="0" y="0" width="90" height="45" />
          </pattern>
          <linearGradient :id="uid + 'stands'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#000" stop-opacity="0.55" />
            <stop offset="1" stop-color="#000" stop-opacity="0.1" />
          </linearGradient>
          <linearGradient :id="uid + 'grass'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#3f7a3a" />
            <stop offset="1" stop-color="#4f9146" />
          </linearGradient>
          <linearGradient :id="uid + 'dirt'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#b98a5e" />
            <stop offset="1" stop-color="#c99a6a" />
          </linearGradient>
          <clipPath :id="uid + 'fair'">
            <polygon :points="geo.fair" />
          </clipPath>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.35" r="0.7">
            <stop offset="0" stop-color="#ffffff" />
            <stop offset="1" stop-color="#d4d4d4" />
          </radialGradient>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" fill="#15151c" />
        <rect v-if="crowd" x="0" y="0" :width="STAGE_W" height="175" :fill="'url(#' + uid + 'crowd)'" opacity="0.85" />
        <rect x="0" y="0" :width="STAGE_W" height="175" :fill="'url(#' + uid + 'stands)'" />

        <!-- Foul ground, then the outfield wall with its ad-boards. -->
        <polygon :points="geo.foulGround" fill="#35683a" />
        <ScrollingBoards :boards="geo.boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#16301f" top-fill="#24472f" />

        <!-- Fair territory: mowed stripes, warning track, infield. -->
        <polygon :points="geo.fair" :fill="'url(#' + uid + 'grass)'" />
        <g :clip-path="'url(#' + uid + 'fair)'" fill="rgba(255,255,255,0.06)">
          <polygon v-for="(s, i) in geo.stripes" :key="'st' + i" :points="s" />
        </g>
        <polygon :points="geo.track" fill="#a87c52" />
        <polygon :points="geo.infieldDirt" :fill="'url(#' + uid + 'dirt)'" />
        <polygon :points="geo.infieldGrass" fill="#4a8a42" />
        <polygon :points="geo.homeCircle" :fill="'url(#' + uid + 'dirt)'" />
        <polygon :points="geo.mound" fill="#c49466" stroke="rgba(0,0,0,0.12)" stroke-width="0.6" />
        <polygon :points="geo.rubber" fill="#fff" />

        <!-- Logo mowed into the outfield grass, clear of the fielders. -->
        <image v-if="showSirplayLogo" :href="PITCH_LOGO" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />

        <!-- Batting side attacking: soft glow on the infield in its colour. -->
        <polygon v-if="!finished" :points="geo.infieldGrass" :fill="batTint" class="bb-attack-glow" />

        <!-- Lines, batter's boxes, foul poles. -->
        <g fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="1" stroke-linejoin="round">
          <path v-for="(d, i) in geo.lines" :key="'ln' + i" :d="d" />
        </g>
        <g stroke="#facc15" stroke-width="1.6" stroke-linecap="round">
          <path v-for="(d, i) in geo.poles" :key="'pole' + i" :d="d" />
        </g>

        <!-- Bases (an occupied one glows in the batting team's colour). -->
        <g v-for="b in baseViews" :key="'base' + b.n">
          <ellipse v-if="b.on" :cx="b.c.x" :cy="b.c.y" :rx="9 * b.c.k + 3" :ry="3.4 * b.c.k + 1.2" :fill="batColor" class="bb-base-glow" />
          <polygon :points="b.points" :fill="b.on ? batColor : '#fff'" stroke="#fff" stroke-width="0.6" />
        </g>
        <polygon :points="geo.plate" fill="#fff" />

        <!-- Where the ball came down. -->
        <ellipse v-if="landing" :key="'land' + landing.key" :cx="landing.x" :cy="landing.y" :rx="landing.rx" :ry="landing.ry" fill="none" stroke="#fff" stroke-width="1" class="bb-landing" />

        <!-- Players, far ones first. -->
        <g v-for="f in figures" :key="f.key" :transform="'translate(' + f.x.toFixed(1) + ',' + f.y.toFixed(1) + ') scale(' + f.k.toFixed(3) + ')'" :opacity="f.o">
          <ellipse cx="0" cy="0" rx="5" ry="1.6" fill="rgba(0,0,0,0.35)" />
          <g :transform="f.crouch ? 'scale(1,0.7)' : null">
            <rect x="-2.8" y="-9" width="5.6" height="9" rx="1.5" fill="#e5e7eb" />
            <rect x="-3.4" y="-16" width="6.8" height="8" rx="2" :fill="f.color" />
            <circle cx="0" cy="-18.4" r="2.6" fill="#d6a77a" />
            <path d="M-2.8,-18.6 a2.8,2.8 0 0 1 5.6,0 z" :fill="f.color" />
          </g>
          <line v-if="f.bat !== null" x1="0" y1="-13" x2="0" y2="-25" :transform="'rotate(' + f.bat.toFixed(0) + ' 0 -13)'" stroke="#d9a066" stroke-width="1.7" stroke-linecap="round" />
        </g>

        <!-- Ball: shadow on the ground + the ball in the air. -->
        <template v-if="ballView">
          <ellipse :cx="ballView.sx" :cy="ballView.sy" :rx="ballView.r * 1.3" :ry="ballView.r * 0.45" fill="rgba(0,0,0,0.35)" />
          <circle :cx="ballView.x" :cy="ballView.y" :r="ballView.r + 1.6" fill="rgba(255,255,255,0.25)" />
          <circle :cx="ballView.x" :cy="ballView.y" :r="ballView.r" :fill="'url(#' + uid + 'ball)'" />
        </template>

        <!-- Home run: fireworks over the wall. -->
        <g v-if="fireworks" :key="'fw' + fireworks">
          <g v-for="(fw, i) in FIREWORKS" :key="'fw' + i" :transform="'translate(' + fw.x + ',' + fw.y + ')'">
            <g class="bb-burst" :style="{ animationDelay: fw.delay + 's' }">
              <line v-for="a in 12" :key="'ray' + a" x1="0" y1="0" :x2="Math.cos(a * Math.PI / 6) * 16" :y2="Math.sin(a * Math.PI / 6) * 16" :stroke="i % 2 ? batColor : '#facc15'" stroke-width="1.6" stroke-linecap="round" />
            </g>
          </g>
        </g>
      </svg>

      <!-- Batter (left) and pitcher (right) with their numbers. -->
      <div class="bb-header">
        <div class="bb-panel is-batter" :style="{ background: 'linear-gradient(90deg, ' + tint(batColor, 0.6) + ' 0%, ' + tint(batColor, 0.12) + ' 100%)' }">
          <svg class="bb-jersey" viewBox="0 0 24 24"><path :d="JERSEY" :fill="batColor" stroke="rgba(255,255,255,0.65)" stroke-width="0.8" /></svg>
          <div class="bb-panel-body">
            <div class="bb-panel-label">{{ $t('baseball.atbat') }}<b v-if="batterInfo.name"> · {{ batterInfo.name }}</b></div>
            <div class="bb-panel-stats">
              <div v-for="s in batterInfo.stats" :key="'b' + s.label" class="bb-stat"><span>{{ s.label }}</span><b>{{ s.value }}</b></div>
            </div>
          </div>
        </div>
        <div class="bb-panel is-pitcher" :style="{ background: 'linear-gradient(270deg, ' + tint(fieldColor, 0.4) + ' 0%, rgba(255,255,255,0.1) 100%)' }">
          <div class="bb-panel-body">
            <div class="bb-panel-label"><b v-if="pitcherInfo.name">{{ pitcherInfo.name }} · </b>{{ $t('baseball.pitcher') }}</div>
            <div class="bb-panel-stats">
              <div v-for="s in pitcherInfo.stats" :key="'p' + s.label" class="bb-stat"><span>{{ s.label }}</span><b>{{ s.value }}</b></div>
            </div>
          </div>
          <svg class="bb-jersey" viewBox="0 0 24 24"><path :d="JERSEY" :fill="fieldColor" stroke="rgba(255,255,255,0.65)" stroke-width="0.8" /></svg>
        </div>
      </div>

      <!-- Inning, bases / outs, count and who is on base. -->
      <div class="bb-status">
        <div class="bb-inning">
          <svg viewBox="0 0 10 6" class="bb-half" :class="{ 'is-on': half === 'top' }"><path d="M5,0 L10,6 L0,6 Z" /></svg>
          <span class="bb-inning-no">{{ inningText }}</span>
          <svg viewBox="0 0 10 6" class="bb-half" :class="{ 'is-on': half === 'bottom' }"><path d="M0,0 L10,0 L5,6 Z" /></svg>
        </div>
        <div class="bb-diamond">
          <svg viewBox="0 0 30 20">
            <rect v-for="d in MINI_BASES" :key="'mb' + d.n" :x="d.x - 3.6" :y="d.y - 3.6" width="7.2" height="7.2" :transform="'rotate(45 ' + d.x + ' ' + d.y + ')'"
                  :fill="view.bases[d.n - 1] ? batColor : 'rgba(255,255,255,0.3)'" />
          </svg>
          <div class="bb-outs">
            <span v-for="n in 3" :key="'out' + n" :class="{ 'is-on': view.outs >= n }"></span>
          </div>
        </div>
        <div class="bb-count" :title="$t('baseball.ball') + ' - ' + $t('baseball.strike')">{{ view.balls }} - {{ view.strikes }}</div>
        <div class="bb-sep"></div>
        <div v-for="n in 3" :key="'bs' + n" class="bb-base-col">
          <span class="bb-base-label">{{ n }}B</span>
          <span class="bb-base-value" :class="{ 'is-on': view.bases[n - 1] }" :style="view.bases[n - 1] ? { borderColor: batColor } : null">
            {{ view.bases[n - 1] ? $t('baseball.occupied') : $t('baseball.empty') }}
          </span>
        </div>
      </div>

      <!-- Play card over the field (the header stays readable). -->
      <transition name="bb-card">
        <div v-if="card" :key="card.key" class="bb-overlay">
          <div class="bb-card" :class="'is-' + card.style" :style="{ '--side-color': card.color, '--side-text': contrastOf(card.color) }">
            <template v-if="card.style === 'pitch'">
              <div class="bb-card-main">
                <div class="bb-card-title">{{ card.title }}</div>
                <svg class="bb-zone" viewBox="0 0 60 64">
                  <rect x="1" y="1" width="58" height="62" fill="none" stroke="rgba(255,255,255,0.35)" stroke-width="0.8" />
                  <rect v-for="c in ZONE_CELLS" :key="'z' + c.i" :x="c.x" :y="c.y" width="10.67" height="14" fill="rgba(190,70,70,0.7)" stroke="rgba(255,255,255,0.5)" stroke-width="0.7" />
                  <circle :cx="card.dot.x" :cy="card.dot.y" r="4.2" fill="#fff" stroke="rgba(0,0,0,0.35)" stroke-width="0.8" class="bb-zone-dot" />
                </svg>
                <div class="bb-card-count">{{ card.count }}</div>
              </div>
              <svg class="bb-batter-art" viewBox="0 0 80 120">
                <line x1="46" y1="36" x2="67" y2="2" stroke="#c98b4f" stroke-width="4.5" stroke-linecap="round" />
                <path d="M30,70 L52,70 L57,120 L45,120 L41,90 L37,120 L23,120 Z" fill="#f1f1f1" stroke="#cfcfcf" stroke-width="0.8" />
                <rect x="29" y="66" width="24" height="5" fill="#1f2937" />
                <path d="M27,42 Q40,35 53,42 L54,68 L28,68 Z" fill="#f5f5f5" />
                <path d="M51,42 L54,68 L50,68 L48,43 Z" :fill="card.color" />
                <circle cx="30" cy="45" r="5.5" :fill="card.color" />
                <circle cx="51" cy="45" r="5.5" :fill="card.color" />
                <line x1="31" y1="47" x2="44" y2="37" stroke="#f5f5f5" stroke-width="6" stroke-linecap="round" />
                <line x1="51" y1="47" x2="48" y2="37" stroke="#f5f5f5" stroke-width="6" stroke-linecap="round" />
                <circle cx="46" cy="36" r="4.2" :fill="card.color" />
                <circle cx="38" cy="30" r="8" fill="#e0b48a" />
                <path d="M28.5,30 A9.8,9.8 0 0 1 48,27 L51,31 L28.5,31 Z" :fill="card.color" />
                <circle cx="41" cy="32" r="3.6" :fill="card.color" />
              </svg>
            </template>
            <template v-else-if="card.style === 'simple'">
              <svg class="bb-card-icon" viewBox="0 0 24 24">
                <line x1="6" y1="19" x2="19" y2="5" stroke="#d9a066" stroke-width="3.2" stroke-linecap="round" />
                <line x1="4.2" y1="20.8" x2="6.4" y2="18.6" stroke="#8b5a2b" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div class="bb-card-title">{{ card.title }}</div>
              <div v-if="card.sub" class="bb-card-sub">{{ card.sub }}</div>
            </template>
            <template v-else>
              <div class="bb-card-badge">{{ card.badge }}</div>
              <div class="bb-card-title">{{ card.title }}</div>
              <div v-if="card.sub" class="bb-card-sub">{{ card.sub }}</div>
              <div v-if="card.score" class="bb-card-score">
                <span :class="{ 'is-won': card.score.side === '1' }">{{ card.score.h }}</span>
                <span class="bb-card-sep">-</span>
                <span :class="{ 'is-won': card.score.side === '2' }">{{ card.score.a }}</span>
              </div>
            </template>
          </div>
        </div>
      </transition>

      <!-- Match over: big badge that stays up, with the line score. -->
      <transition name="bb-final">
        <div v-if="finished" class="bb-final">
          <div class="bb-final-badge">
            <svg class="bb-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="bb-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div class="bb-final-score">
              <span :class="{ 'is-win': score.h > score.a }">{{ homeName }} {{ score.h }}</span>
              <span class="bb-card-sep">-</span>
              <span :class="{ 'is-win': score.a > score.h }">{{ score.a }} {{ awayName }}</span>
            </div>
            <div v-if="lineScore.length" class="bb-final-innings">
              <span v-for="(q, i) in lineScore" :key="'ls' + i">{{ q.h }}-{{ q.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { pt, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width (see stageScaleMixin).
const STAGE_W = 460;
const STAGE_H = 290;
// Camera behind home plate, looking at centre field. Field point (x, y, h)
// in metres: x across (right field > 0), y from home plate towards centre
// field, h height. Tuned so home plate sits near the bottom of the stage and
// the centre-field wall just under the status bar.
const CX = 230;
const HORIZON = 118;
const CAM_D = 25.8;
const CAM_F = 294;
const CAM_HF = 3922;
function p3(x, y, h = 0) {
  const z = Math.max(1, y + CAM_D);
  const s = CAM_F / z;
  return { x: CX + x * s, y: HORIZON + CAM_HF / z - h * s, k: s * CAM_D / CAM_F };
}
const poly = list => list.map(p => pt(p3(p.x, p.y, p.h || 0))).join(' ');
const pathOf = list => 'M' + list.map(p => pt(p3(p.x, p.y, p.h || 0))).join(' L');

// Diamond: 90 ft between bases.
const B = 19.4;
const BASES = [{ x: 0, y: 0 }, { x: B, y: B }, { x: 0, y: 2 * B }, { x: -B, y: B }, { x: 0, y: 0 }];
const MOUND = { x: 0, y: 18.4 };
const PLATE = { x: 0, y: 0 };
// Wall distance along the direction a (radians from the centre-field
// axis): 99 m down the lines, 122 m to centre field.
const fenceR = a => 99 + 23 * Math.cos(2 * a);
const fenceAt = (a, inset = 0) => ({ x: (fenceR(a) - inset) * Math.sin(a), y: (fenceR(a) - inset) * Math.cos(a) });
const QUARTER = Math.PI / 4;
// Defensive positions.
const FIELD_POS = {
  P: { x: 0, y: 18.2 }, C: { x: 0, y: -1.4 },
  '1B': { x: 20.5, y: 24 }, '2B': { x: 8, y: 36 }, SS: { x: -9, y: 35 }, '3B': { x: -20.5, y: 24 },
  LF: { x: -42, y: 68 }, CF: { x: 0, y: 88 }, RF: { x: 42, y: 68 }
};
const INFIELDERS = ['1B', '2B', 'SS', '3B'];
const BATTER_SPOT = { x: -1.15, y: 0.15 };
const BAT_IDLE = -35;

// LSports statistics used to tell the game (per team).
const ST = {
  HITS: 33, DOUBLES: 2080, TRIPLES: 2081, HOMERUNS: 2082, STRIKEOUTS: 2083, WALKS: 2084, HIT_BY_PITCH: 2446,
  OUTS: 2087, STOLEN: 2097, CAUGHT: 2544, ERRORS: 2096, STRIKES: 2262, BALLS: 2398, PLATE_APP: 2545,
  AT_BATS: 2085, RBI: 2086, INNINGS_PITCHED: 2550, HITS_ALLOWED: 2263, WALKS_ALLOWED: 2556, WALKS_ALLOWED_RAW: 2273
};
// LSports player statistics.
const PS = {
  P_WALKS: 1009, P_STRIKEOUTS: 1010, P_INNINGS: 1011, P_HITS: 1012,
  B_AT_BATS: 1017, B_RUNS: 1018, B_HITS: 1019, B_RBI: 1020, B_PLATE_APP: 2378, PITCHES: 2287
};
const TEAM_TYPES = Object.values(ST);
const PLAYER_TYPES = Object.values(PS);
const PITCHER_TYPES = [PS.PITCHES, PS.P_INNINGS, PS.P_STRIKEOUTS, PS.P_WALKS, PS.P_HITS];
// Stats that only move for the team at bat.
const OFFENSE = [ST.HITS, ST.HOMERUNS, ST.STRIKEOUTS, ST.WALKS, ST.HIT_BY_PITCH, ST.STOLEN, ST.CAUGHT, ST.PLATE_APP, ST.AT_BATS];

// Timing of each play (ms): total length and when its card comes up.
const TIMING = {
  atbat: { dur: 1700, cardAt: 0 },
  strike: { dur: 2000, cardAt: 520 },
  ball: { dur: 2000, cardAt: 520 },
  strikeout: { dur: 2400, cardAt: 650 },
  walk: { dur: 2500, cardAt: 550 },
  hbp: { dur: 2500, cardAt: 550 },
  hit: { dur: 3300, cardAt: 1500 },
  homerun: { dur: 4600, cardAt: 1700 },
  out: { dur: 2700, cardAt: 1300 },
  steal: { dur: 2300, cardAt: 1100 },
  caught: { dur: 2300, cardAt: 1100 },
  error: { dur: 1800, cardAt: 0 },
  run: { dur: 2400, cardAt: 900 },
  inning: { dur: 2400, cardAt: 0 },
  sync: { dur: 0, cardAt: 0 }
};
// Plays whose new state (count, outs, inning) shows as soon as the card
// does; the others wait for the runners to arrive.
const APPLY_AT_CARD = ['atbat', 'strike', 'ball', 'strikeout', 'out', 'error', 'inning'];
const PITCH_MS = 460;
const CONTACT_MS = 470;
// Beyond this many queued plays (e.g. the tab was in the background) the
// backlog is dropped and the scene jumps to the live state.
const MAX_QUEUE = 8;

const JERSEY = 'M8,3 L4,5 L1,9 L4,11.5 L5.5,10 L5.5,21 L18.5,21 L18.5,10 L20,11.5 L23,9 L20,5 L16,3 Q12,6.5 8,3 Z';
const MINI_BASES = [{ n: 1, x: 22, y: 11 }, { n: 2, x: 15, y: 5 }, { n: 3, x: 8, y: 11 }];
const ZONE_CELLS = [0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => ({ i, x: 14 + (i % 3) * 10.67, y: 10 + Math.floor(i / 3) * 14 }));
const FIREWORKS = [{ x: 120, y: 118, delay: 0 }, { x: 230, y: 108, delay: 0.35 }, { x: 340, y: 118, delay: 0.7 }, { x: 175, y: 100, delay: 1.05 }, { x: 290, y: 100, delay: 1.3 }];

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const ease = t => t * t * (3 - 2 * t);
const other = s => (s === '1' ? '2' : '1');
const copyState = m => ({ ...m, bases: m.bases.slice() });
const numOrNull = v => { const n = parseFloat(v); return isNaN(n) ? null : n; };

// ---- Feed readers -------------------------------------------------------
const normName = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const truthy = v => ['1', 'true', 'yes', 'y', 'occupied', 'x'].includes(String(v).toLowerCase().trim());
// Runners on base from a single extra-data value: "1/0/1", "101", "1,0,1",
// "1B,3B", "first,third" or a bitmask (1 = first, 2 = second, 4 = third).
function parseBases(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).toLowerCase().trim();
  if (/first|second|third|1b|2b|3b|1st|2nd|3rd/.test(s)) return [/first|1b|1st/.test(s), /second|2b|2nd/.test(s), /third|3b|3rd/.test(s)];
  if (/^[01]{3}$/.test(s)) return s.split('').map(c => c === '1');
  const parts = s.split(/[^a-z0-9]+/).filter(Boolean);
  if (parts.length === 3) return parts.map(truthy);
  if (/^[0-7]$/.test(s)) { const n = parseInt(s); return [!!(n & 1), !!(n & 2), !!(n & 4)]; }
  return null;
}
function readExtra(ls) {
  const list = ls.LivescoreExtraData || ls.livescoreExtraData || [];
  const map = {};
  list.forEach(e => { if (e) map[normName(e.Name ?? e.name)] = e.Value ?? e.value; });
  const pick = keys => {
    for (const k of keys) {
      const v = map[k];
      if (v !== undefined && v !== null && String(v) !== '') return String(v);
    }
    return null;
  };
  const num = keys => { const n = parseInt(pick(keys)); return isNaN(n) ? null : n; };
  let bases = parseBases(pick(['bases', 'basesoccupied', 'runners', 'runnersonbase', 'baserunners', 'onbase']));
  if (!bases) {
    const single = [
      pick(['firstbase', 'base1', '1stbase', 'runneronfirst', 'onfirst']),
      pick(['secondbase', 'base2', '2ndbase', 'runneronsecond', 'onsecond']),
      pick(['thirdbase', 'base3', '3rdbase', 'runneronthird', 'onthird'])
    ];
    if (single.some(v => v !== null)) bases = single.map(v => v !== null && truthy(v));
  }
  const side = v => {
    const s = String(v || '').toLowerCase();
    if (s === '1' || s === 'home') return '1';
    if (s === '2' || s === 'away') return '2';
    return null;
  };
  const halfRaw = String(pick(['inninghalf', 'half', 'halfinning', 'inningpart', 'topbottom', 'inningstate']) || '').toLowerCase();
  return {
    balls: num(['balls', 'ball', 'ballscount', 'currentballs']),
    strikes: num(['strikes', 'strike', 'strikescount', 'currentstrikes']),
    outs: num(['outs', 'out', 'outscount', 'currentouts']),
    bases,
    turn: side(pick(['turn', 'atbat', 'battingteam', 'offense'])),
    half: halfRaw.includes('top') ? 'top' : (halfRaw.includes('bot') ? 'bottom' : null),
    batter: pick(['batter', 'currentbatter', 'batsman', 'hitter']),
    pitcher: pick(['pitcher', 'currentpitcher'])
  };
}
const statValue = s => {
  const v = s.Value ?? s.value;
  if (v !== undefined) return numOrNull(v);
  const r = (s.Results || s.results || [])[0];
  return r ? numOrNull(r.Value ?? r.value) : null;
};

export default {
  name: 'BaseballArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    // The event's livescore (Scoreboard, Periods, Statistics,
    // PlayerStatistics, LivescoreExtraData).
    livescore: { type: Object, default: null },
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    homeId: { type: [String, Number], default: '' },
    awayId: { type: [String, Number], default: '' },
    finished: { type: Boolean, default: false },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    const empty = { bases: [false, false, false], balls: 0, strikes: 0, outs: 0, bat: null, inning: 1 };
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM: 1,
      JERSEY,
      MINI_BASES,
      ZONE_CELLS,
      FIREWORKS,
      crowd: '',
      raf: null,
      now: 0,
      lastFeed: null,
      // model: the game as the feed says it is now; view: what the scene
      // shows (lags the model while plays are animated).
      model: copyState(empty),
      view: copyState(empty),
      queue: [],
      ev: null,
      evT: 0,
      cardOn: false,
      // Players: who is at bat / pitching for each side, and who just
      // finished a plate appearance.
      batterId: { 1: null, 2: null },
      pitcherId: { 1: null, 2: null },
      lastPaPlayer: { 1: null, 2: null },
      lastAtbatAt: 0
    };
  },
  watch: {
    feedKey: {
      handler() { this.onFeed(this.feed); },
      immediate: true
    },
    finished(val) {
      if (val) {
        this.queue = [];
        this.ev = null;
        this.cardOn = false;
        this.view = copyState(this.model);
      }
    }
  },
  mounted() {
    this.crowd = crowdTexture('cool');
    this.raf = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
  },
  methods: {
    tint(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return `rgba(45,95,174,${alpha})`;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    },
    // Readable text on top of a team colour (dark text on light shirts).
    contrastOf(hex) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return '#fff';
      const n = parseInt(m[1], 16);
      const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
      return lum > 0.65 ? '#111827' : '#fff';
    },
    teamName(side) { return side === '1' ? this.homeName : this.awayName; },
    colorOf(side) { return side === '1' ? this.homeCol : this.awayCol; },
    // ---- Animation loop ---------------------------------------------------
    tick(ts) {
      this.now = ts;
      if (!this.ev && this.queue.length) {
        const ev = this.queue.shift();
        // A backlog plays faster.
        ev.speed = this.queue.length > 2 ? 0.6 : 1;
        ev.t0 = ts;
        ev.applied = false;
        this.ev = ev;
        if (ev.applyAtStart) this.applyEvent(ev);
      }
      const ev = this.ev;
      if (ev) {
        const t = (ts - ev.t0) / ev.speed;
        this.evT = t;
        const cardOn = !!ev.card && t >= ev.cardAt && t < ev.dur - 250;
        if (cardOn !== this.cardOn) this.cardOn = cardOn;
        if (!ev.applied && t >= ev.applyAt) this.applyEvent(ev);
        if (t >= ev.dur) {
          if (!ev.applied) this.applyEvent(ev);
          this.ev = null;
          this.cardOn = false;
        }
      }
      this.raf = requestAnimationFrame(this.tick);
    },
    applyEvent(ev) {
      ev.applied = true;
      this.view = copyState(ev.after);
    },
    // ---- Feed -------------------------------------------------------------
    feedBatSide(f) {
      if (f.extra.turn) return f.extra.turn;
      if (f.extra.half) return f.extra.half === 'top' ? '2' : '1';
      return null;
    },
    initFrom(f) {
      const bat = this.feedBatSide(f) || '2';
      let outs = f.extra.outs;
      if (outs === null) {
        const total = f.stats[bat][ST.OUTS];
        outs = total !== null && total !== undefined ? total % 3 : 0;
      }
      this.model = {
        bases: f.extra.bases || [false, false, false],
        balls: f.extra.balls ?? 0,
        strikes: f.extra.strikes ?? 0,
        outs,
        bat,
        inning: f.period >= 1 && f.period <= 20 ? f.period : (f.period === 40 ? 10 : 1)
      };
      this.view = copyState(this.model);
      // Pitcher on the mound: the last one listed with pitching numbers.
      ['1', '2'].forEach(side => {
        const pitchers = f.players.filter(p => p.side === side && PITCHER_TYPES.some(t => p.st[t] !== undefined));
        if (pitchers.length) this.pitcherId[side] = pitchers[pitchers.length - 1].id;
      });
    },
    // Who batted / pitched in this update, from the player statistics.
    trackPlayers(prev, f) {
      const before = {};
      prev.players.forEach(p => { before[p.id] = p; });
      f.players.forEach(p => {
        const o = before[p.id];
        if (!o || (p.side !== '1' && p.side !== '2')) return;
        const up = t => (p.st[t] ?? 0) > (o.st[t] ?? 0);
        if (up(PS.B_PLATE_APP) || up(PS.B_AT_BATS)) {
          this.lastPaPlayer[p.side] = p.id;
          // Next one up: the following batter in the listed order.
          const batters = f.players.filter(x => x.side === p.side && (x.st[PS.B_PLATE_APP] !== undefined || x.st[PS.B_AT_BATS] !== undefined));
          const i = batters.findIndex(x => x.id === p.id);
          this.batterId[p.side] = batters.length > 1 && i >= 0 ? batters[(i + 1) % batters.length].id : null;
        }
        if (PITCHER_TYPES.some(up)) this.pitcherId[p.side] = p.id;
      });
    },
    onFeed(f) {
      if (!f) return;
      if (!this.lastFeed) {
        this.lastFeed = f;
        this.initFrom(f);
        return;
      }
      const prev = this.lastFeed;
      this.lastFeed = f;
      if (this.finished) {
        this.model.bases = f.extra.bases || this.model.bases;
        return;
      }
      this.trackPlayers(prev, f);
      // How much a team stat went up (a stat appearing for the first time
      // only counts when it's the first one of the game).
      const d = (s, t) => {
        const a = prev.stats[s][t];
        const b = f.stats[s][t];
        if (b === null || b === undefined) return 0;
        if (a === null || a === undefined) return b === 1 ? 1 : 0;
        return Math.max(0, b - a);
      };
      const runsOf = s => Math.max(0, s === '1' ? f.score.h - prev.score.h : f.score.a - prev.score.a);
      const offense = s => OFFENSE.some(t => d(s, t) > 0) || runsOf(s) > 0;
      const m = this.model;
      const events = [];
      const feedBat = this.feedBatSide(f);
      // Side of the plays in this update: the team whose batting numbers
      // moved (the one at bat if both did).
      let s = m.bat;
      if (offense('1') !== offense('2')) s = offense('1') ? '1' : '2';
      else if (!s) s = feedBat || '2';
      if (!m.bat) m.bat = s;
      // The other team is batting and nobody said so: the half-inning
      // changed before these plays.
      if (s !== m.bat) events.push(this.mkInning(s, f));
      this.buildPlays(s, d, runsOf(s), prev, f, events);
      if (feedBat && feedBat !== m.bat) events.push(this.mkInning(feedBat, f));
      else if (!feedBat && m.outs >= 3) events.push(this.mkInning(other(m.bat), f));
      // What the feed states directly wins over the simulation.
      const x = f.extra;
      if (x.bases) m.bases = x.bases.slice();
      if (x.balls !== null) m.balls = x.balls;
      if (x.strikes !== null) m.strikes = x.strikes;
      if (x.outs !== null && !(events.length && events[events.length - 1].kind === 'inning' && x.outs >= 3)) m.outs = x.outs;
      // The pitch card shows the count the feed states, when it does.
      const lastPitch = events.filter(e => e.kind === 'strike' || e.kind === 'ball').pop();
      if (lastPitch && (x.balls !== null || x.strikes !== null)) lastPitch.card.count = `${m.balls} - ${m.strikes}`;
      if (events.length) events[events.length - 1].after = copyState(m);
      else if (this.ev || this.queue.length) events.push(this.mkEvent('sync', m.bat, {}));
      else this.view = copyState(m);
      this.enqueue(events);
    },
    enqueue(events) {
      if (!events.length) return;
      if (this.queue.length + events.length > MAX_QUEUE) {
        this.queue = [];
        this.ev = null;
        this.cardOn = false;
        this.view = copyState(this.model);
        return;
      }
      this.queue.push(...events);
    },
    // Plays of one update for the team at bat, in the order they happen on
    // the field; each one moves the model on.
    buildPlays(s, d, runs, prev, f, events) {
      const both = t => d('1', t) + d('2', t);
      const exUp = k => (f.extra[k] !== null && prev.extra[k] !== null && f.extra[k] > prev.extra[k] ? f.extra[k] - prev.extra[k] : 0);
      const hr = d(s, ST.HOMERUNS);
      const triples = d(s, ST.TRIPLES);
      const doubles = d(s, ST.DOUBLES);
      const hits = d(s, ST.HITS);
      const singles = Math.max(0, hits - hr - triples - doubles);
      const k = d(s, ST.STRIKEOUTS);
      const bb = d(s, ST.WALKS);
      const hbp = d(s, ST.HIT_BY_PITCH);
      const sb = d(s, ST.STOLEN);
      const cs = d(s, ST.CAUGHT);
      const errors = both(ST.ERRORS);
      const outs = Math.max(both(ST.OUTS), exUp('outs'));
      const plainOuts = Math.max(0, outs - k - cs);
      const hitBases = triples ? 3 : (doubles ? 2 : (singles ? 1 : 0));
      const ended = !!(hr || hitBases || k || bb || hbp || plainOuts);
      const paOnly = !ended && (d(s, ST.PLATE_APP) > 0 || d(s, ST.AT_BATS) > 0);
      const push = ev => { if (ev) events.push(ev); };
      // A single pitch: only when it didn't end the plate appearance (the
      // strikeout / walk card already tells that pitch).
      if (!ended) {
        if (both(ST.BALLS) > 0 || exUp('balls') > 0) push(this.mkEvent('ball', s, {}));
        if (both(ST.STRIKES) > 0 || exUp('strikes') > 0) push(this.mkEvent('strike', s, {}));
      }
      let left = runs;
      const take = ev => { if (ev) { left = Math.max(0, left - (ev.runs || 0)); push(ev); } };
      if (hr) take(this.mkEvent('homerun', s, { runs: left }));
      else if (hitBases) take(this.mkEvent('hit', s, { n: hitBases, runs: left }));
      else if (bb) take(this.mkEvent('walk', s, { runs: left }));
      else if (hbp) take(this.mkEvent('hbp', s, { runs: left }));
      if (k) push(this.mkEvent('strikeout', s, {}));
      if (plainOuts) push(this.mkEvent('out', s, { n: plainOuts }));
      if (sb) take(this.mkEvent('steal', s, { runs: left }));
      if (cs) push(this.mkEvent('caught', s, {}));
      if (errors) push(this.mkEvent('error', other(s), {}));
      if (left > 0) take(this.mkEvent('run', s, { runs: left }));
      // Next batter up (once, even if the plate appearance and its result
      // arrive in separate updates).
      const nowMs = Date.now();
      if ((ended && this.model.outs < 3) || (paOnly && nowMs - this.lastAtbatAt > 4000)) {
        push(this.mkEvent('atbat', s, {}));
        this.lastAtbatAt = nowMs;
      }
    },
    mkInning(side, f) {
      return this.mkEvent('inning', side, { period: f.period });
    },
    // ---- Plays: simulation + plan of the animation ------------------------
    mkEvent(kind, side, opts) {
      const m = this.model;
      const tm = TIMING[kind];
      const ev = {
        key: kind + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
        kind,
        side,
        dur: tm.dur,
        cardAt: tm.cardAt,
        applyAt: APPLY_AT_CARD.includes(kind) ? tm.cardAt : tm.dur,
        applyAtStart: kind === 'inning',
        moves: [],
        runs: 0,
        plan: {},
        card: null
      };
      const fieldSide = other(side);
      const color = this.colorOf(side);
      const who = this.lastPaPlayer[side] && this.playerById(this.lastPaPlayer[side]);
      const sub = who ? `${who.name} · ${this.teamName(side)}` : this.teamName(side);
      const resetCount = () => { m.balls = 0; m.strikes = 0; };
      const scoreCard = () => ({ h: this.score.h, a: this.score.a, side: ev.runs ? side : null });
      const result = (badge, title, withScore) => ({ style: 'result', badge, title, sub, color, score: withScore ? scoreCard() : null });
      switch (kind) {
        case 'strike':
        case 'ball': {
          const strike = kind === 'strike';
          if (strike) m.strikes = Math.min(2, m.strikes + 1);
          else m.balls = Math.min(3, m.balls + 1);
          ev.plan = this.pitchPlan(strike);
          if (strike && Math.random() < 0.5) ev.plan.swingAt = 330;
          ev.card = { style: 'pitch', title: this.$t('baseball.' + kind), color, count: `${m.balls} - ${m.strikes}`, dot: ev.plan.dot };
          break;
        }
        case 'strikeout':
          m.outs += 1;
          resetCount();
          ev.plan = { ...this.pitchPlan(true), swingAt: 330 };
          ev.card = result('K', this.$t('baseball.strikeout'));
          ev.card.sub = `${this.outsText(m.outs)} · ${sub}`;
          break;
        case 'walk':
        case 'hbp':
          resetCount();
          ev.plan = this.pitchPlan(false);
          ev.moves = this.forceMoves(m.bases, 500, 700);
          this.finishMoves(ev, opts.runs);
          ev.card = result(kind === 'walk' ? 'BB' : 'HBP', this.$t('baseball.' + kind), ev.runs > 0);
          break;
        case 'hit': {
          resetCount();
          ev.plan = { ...this.pitchPlan(true), swingAt: 380, ...this.hitFlight(opts.n) };
          ev.moves = this.hitMoves(m.bases, opts.n, opts.runs, 800, 520);
          this.finishMoves(ev, opts.runs);
          const name = ['', 'single', 'double', 'triple'][opts.n];
          ev.card = result(opts.n + 'B', this.$t('baseball.' + name), ev.runs > 0);
          break;
        }
        case 'homerun':
          resetCount();
          ev.plan = { ...this.pitchPlan(true), swingAt: 380, ...this.homerunFlight() };
          ev.moves = this.hitMoves(m.bases, 4, 0, 1000, 600);
          this.finishMoves(ev, 0);
          ev.card = result(ev.runs > 1 ? '+' + ev.runs : 'HR', this.$t('baseball.homerun'), true);
          break;
        case 'out':
          m.outs += opts.n;
          resetCount();
          ev.plan = { ...this.pitchPlan(true), swingAt: 380, ...this.outFlight() };
          ev.card = result('OUT', this.$t('baseball.out'));
          ev.card.sub = `${this.outsText(Math.min(3, m.outs))} · ${sub}`;
          break;
        case 'steal':
        case 'caught': {
          const caught = kind === 'caught';
          const from = [2, 1, 3].find(b => m.bases[b - 1] && (b === 3 ? (caught || opts.runs > 0) : !m.bases[b]));
          if (from) {
            ev.moves = [{ from, to: from + 1, start: 150, per: 1100, out: caught }];
            ev.plan.throw = { from: { x: 0, y: -1.4, h: 1.4 }, to: { ...BASES[from + 1], h: 1.2 }, start: 600, dur: 520, peak: 1.2 };
          }
          if (caught) m.outs += 1;
          this.finishMoves(ev, caught ? 0 : opts.runs);
          ev.card = result(caught ? 'CS' : 'SB', this.$t('baseball.' + (caught ? 'caughtstealing' : 'stolenbase')), ev.runs > 0);
          ev.card.sub = this.teamName(side);
          break;
        }
        case 'error':
          ev.card = { style: 'result', badge: 'E', title: this.$t('baseball.error'), sub: this.teamName(side), color };
          break;
        case 'run': {
          const runners = [3, 2, 1].filter(b => m.bases[b - 1]).slice(0, opts.runs);
          ev.moves = runners.map((b, i) => ({ from: b, to: 4, start: 150 + i * 250, per: 900 / Math.max(1, 4 - b) }));
          this.finishMoves(ev, opts.runs);
          ev.runs = opts.runs;
          ev.card = result('+' + opts.runs, this.$t(opts.runs > 1 ? 'baseball.runs' : 'baseball.run'), true);
          ev.card.sub = this.teamName(side);
          ev.card.score.side = side;
          break;
        }
        case 'inning': {
          const prevBat = m.bat;
          m.bat = side;
          m.bases = [false, false, false];
          m.outs = 0;
          resetCount();
          // Top of a new inning after the home half; the feed's period
          // wins when it is already further on.
          const next = side === '2' && prevBat === '1' ? m.inning + 1 : m.inning;
          m.inning = opts.period >= 1 && opts.period <= 20 ? Math.max(next, opts.period) : next;
          const half = side === '2' ? 'top' : 'bottom';
          ev.card = {
            style: 'result',
            badge: (half === 'top' ? '▲' : '▼') + m.inning,
            title: this.$t('baseball.inning', { n: m.inning }),
            sub: `${this.$t('baseball.' + half)} · ${this.teamName(side)} ${this.$t('baseball.batting')}`,
            color
          };
          break;
        }
        case 'atbat': {
          const next = this.batterId[side] && this.playerById(this.batterId[side]);
          ev.card = { style: 'simple', title: this.$t('baseball.atbat'), sub: next ? next.name : this.teamName(side), color };
          break;
        }
        default:
          break;
      }
      if (ev.moves.length) {
        const end = Math.max(...ev.moves.map(mv => mv.start + mv.per * Math.max(1, mv.to - mv.from) + 350));
        ev.dur = Math.max(ev.dur, end);
        if (!APPLY_AT_CARD.includes(kind)) ev.applyAt = ev.dur;
      }
      ev.fieldSide = fieldSide;
      ev.after = copyState(m);
      return ev;
    },
    outsText(n) {
      return `${n} ${this.$t('baseball.out')}`;
    },
    // Pitch from the mound to the plate; the card's strike-zone dot matches
    // where it arrives.
    pitchPlan(strike) {
      let zx, zy;
      if (strike) {
        zx = rand(-0.9, 0.9);
        zy = rand(-0.9, 0.9);
      } else if (Math.random() < 0.5) {
        zx = (Math.random() < 0.5 ? -1 : 1) * rand(1.25, 1.55);
        zy = rand(-1.2, 1.2);
      } else {
        zx = rand(-1.2, 1.2);
        zy = (Math.random() < 0.5 ? -1 : 1) * rand(1.2, 1.35);
      }
      return {
        pitch: { from: { x: 0.35, y: 17.6, h: 2 }, to: { x: zx * 0.25, y: -0.3, h: 0.8 + zy * 0.3 }, start: 0, dur: PITCH_MS },
        dot: { x: 30 + zx * 16, y: 31 - zy * 21 },
        swingAt: null
      };
    },
    // Batted ball of a hit: into the outfield, deeper for extra bases; the
    // nearest fielder chases it.
    hitFlight(n) {
      const side = Math.random() < 0.5 ? -1 : 1;
      let a, dist, peak, dur, bounce = false;
      if (n === 1) {
        a = rand(-0.62, 0.62);
        dist = rand(46, 60);
        bounce = Math.random() < 0.5;
        peak = bounce ? 0.6 : 4;
        dur = 900;
      } else if (n === 2) {
        a = side * rand(0.3, 0.6);
        dist = fenceR(a) - rand(6, 12);
        peak = 9;
        dur = 1150;
      } else {
        a = side * rand(0.62, 0.72);
        dist = fenceR(a) - rand(2, 5);
        peak = 10;
        dur = 1250;
      }
      const to = { x: dist * Math.sin(a), y: dist * Math.cos(a), h: 0 };
      return {
        flight: { from: { x: 0.4, y: 0.6, h: 1 }, to, peak, start: CONTACT_MS, dur, bounce },
        chaser: { pos: this.nearestFielder(to), to, start: CONTACT_MS, dur: dur * 1.3 },
        land: to
      };
    },
    homerunFlight() {
      const a = rand(-0.6, 0.6);
      const dist = fenceR(a) + 16;
      const to = { x: dist * Math.sin(a), y: dist * Math.cos(a), h: 6 };
      return {
        // Arc kept low enough to stay under the status bar all the way
        // into the stands.
        flight: { from: { x: 0.4, y: 0.6, h: 1 }, to, peak: 14, start: CONTACT_MS, dur: 1500 },
        chaser: { pos: this.nearestFielder(to), to: fenceAt(a, 2), start: CONTACT_MS, dur: 1500 },
        fireworksAt: CONTACT_MS + 1300
      };
    },
    // Fly ball caught by the nearest fielder, or a grounder to an infielder
    // thrown on to first.
    outFlight() {
      if (Math.random() < 0.55) {
        const a = rand(-0.65, 0.65);
        const dist = rand(55, 85);
        const to = { x: dist * Math.sin(a), y: dist * Math.cos(a), h: 1.8 };
        return {
          flight: { from: { x: 0.4, y: 0.6, h: 1 }, to, peak: 12, start: CONTACT_MS, dur: 1350 },
          chaser: { pos: this.nearestFielder(to), to, start: CONTACT_MS, dur: 1200 },
          caught: true
        };
      }
      const pos = INFIELDERS[Math.floor(Math.random() * INFIELDERS.length)];
      const f = FIELD_POS[pos];
      const to = { x: f.x * 0.94, y: f.y * 0.94, h: 0.3 };
      const dur = 650;
      return {
        flight: { from: { x: 0.4, y: 0.6, h: 0.8 }, to, peak: 0.6, start: CONTACT_MS, dur, bounce: true },
        chaser: { pos, to, start: CONTACT_MS, dur: dur * 0.8 },
        throw: pos === '1B' ? null : { from: { ...to, h: 1.3 }, to: { x: B, y: B, h: 1.3 }, start: CONTACT_MS + dur + 120, dur: 480, peak: 1 },
        caught: true
      };
    },
    nearestFielder(to) {
      let best = 'CF';
      let bd = Infinity;
      Object.keys(FIELD_POS).forEach(k => {
        if (k === 'C' || k === 'P') return;
        const p = FIELD_POS[k];
        const dd = Math.hypot(p.x - to.x, p.y - to.y);
        if (dd < bd) { bd = dd; best = k; }
      });
      return best;
    },
    // Runners on a hit (n bases, 4 = home run): everybody moves up n bases;
    // runs the scoreboard adds on top come from the lead runners.
    hitMoves(bases, n, runs, start, per) {
      const moves = [3, 2, 1].filter(b => bases[b - 1]).map(b => ({ from: b, to: Math.min(4, b + n), start, per }));
      moves.push({ from: 0, to: n, start, per });
      let scored = moves.filter(mv => mv.to === 4).length;
      moves.forEach(mv => {
        if (scored < runs && mv.to < 4 && mv.from > 0) { mv.to = 4; scored++; }
      });
      return moves;
    },
    // Walk / hit by pitch: the batter takes first, forcing the runners behind.
    forceMoves(bases, start, per) {
      const moves = [{ from: 0, to: 1, start, per }];
      if (bases[0]) {
        moves.push({ from: 1, to: 2, start, per });
        if (bases[1]) {
          moves.push({ from: 2, to: 3, start, per });
          if (bases[2]) moves.push({ from: 3, to: 4, start, per });
        }
      }
      return moves;
    },
    // Moves -> bases after the play, runs scored; extra runs from the
    // scoreboard (e.g. a wild pitch in the same update) score the lead runners.
    finishMoves(ev, runs) {
      const m = this.model;
      const bases = m.bases.slice();
      ev.moves.forEach(mv => { if (mv.from > 0) bases[mv.from - 1] = false; });
      ev.moves.forEach(mv => { if (!mv.out && mv.to >= 1 && mv.to <= 3) bases[mv.to - 1] = true; });
      ev.runs = ev.moves.filter(mv => mv.to === 4 && !mv.out).length;
      if (runs > ev.runs) {
        [3, 2, 1].forEach(b => {
          if (ev.runs < runs && bases[b - 1] && !ev.moves.some(mv => mv.to === b)) {
            bases[b - 1] = false;
            ev.moves.push({ from: b, to: 4, start: 150, per: 700 });
            ev.runs++;
          }
        });
      }
      m.bases = bases;
    },
    playerById(id) {
      return (this.lastFeed && this.lastFeed.players.find(p => p.id === String(id))) || null;
    },
    findPlayer(side, key) {
      if (!key || !this.lastFeed) return null;
      const k = String(key).toLowerCase();
      return this.lastFeed.players.find(p => p.side === side && (p.id === String(key) || p.name.toLowerCase() === k)) || null;
    },
    // Ball position (field metres) at time t of a segment, or null outside it.
    segAt(sg, t) {
      if (!sg || t < sg.start || t > sg.start + sg.dur) return null;
      const u = (t - sg.start) / sg.dur;
      const x = lerp(sg.from.x, sg.to.x, u);
      const y = lerp(sg.from.y, sg.to.y, u);
      let h = lerp(sg.from.h, sg.to.h, u) + 4 * (sg.peak || 0) * u * (1 - u);
      if (sg.bounce) h = lerp(sg.from.h, sg.to.h, u) * (1 - u) + Math.abs(Math.sin(u * Math.PI * 3)) * 1.2 * (1 - u);
      return { x, y, h };
    },
    runnerAt(mv, t) {
      const steps = Math.max(1, mv.to - mv.from);
      const u = clamp((t - mv.start) / (mv.per * steps), 0, 1);
      const pos = u * steps;
      const i = Math.min(steps - 1, Math.floor(pos));
      const a = BASES[mv.from + i];
      const b = BASES[mv.from + i + 1];
      const e = pos - i;
      let o = 1;
      if (mv.to === 4 && u >= 1) o = clamp(1 - (t - mv.start - mv.per * steps) / 300, 0, 1);
      if (mv.out && u > 0.75) o = clamp(1 - (u - 0.75) / 0.25, 0.15, 1);
      return { x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e), o };
    }
  },
  computed: {
    logoDecal() {
      // db < 0: y grows away from the camera, i.e. up the screen.
      return floorLogo(p3, 0, 55, -13);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'bb' + this._uid + '-'; },
    homeCol() { return this.homeColor || '#c0392b'; },
    awayCol() { return this.awayColor || '#2d5fae'; },
    batSide() { return this.view.bat || '2'; },
    batColor() { return this.colorOf(this.batSide); },
    fieldColor() { return this.colorOf(other(this.batSide)); },
    batTint() { return this.tint(this.batColor, 0.12); },
    // Everything read from the livescore, as plain values (its JSON is the
    // change detector).
    feed() {
      const ls = this.livescore;
      if (!ls) return null;
      const sb = ls.Scoreboard || ls.scoreboard || {};
      const resOf = list => {
        const out = { h: 0, a: 0 };
        (list || []).forEach(r => {
          const v = parseInt(r.Value ?? r.value);
          const pos = String(r.Position ?? r.position);
          if (!isNaN(v) && pos === '1') out.h = v;
          if (!isNaN(v) && pos === '2') out.a = v;
        });
        return out;
      };
      const stats = { 1: {}, 2: {} };
      (ls.Statistics || ls.statistics || []).forEach(s => {
        const t = parseInt(s.Type ?? s.type);
        if (!TEAM_TYPES.includes(t)) return;
        (s.Results || s.results || []).forEach(r => {
          const pos = String(r.Position ?? r.position);
          if (pos === '1' || pos === '2') stats[pos][t] = numOrNull(r.Value ?? r.value);
        });
      });
      const homeId = String(this.homeId);
      const awayId = String(this.awayId);
      const players = (ls.PlayerStatistics || ls.playerStatistics || []).map(p => {
        let side = String(p.ParticipantPosition ?? p.participantPosition ?? p.Position ?? p.position ?? '');
        if (side !== '1' && side !== '2') {
          const team = String(p.TeamId ?? p.teamId ?? p.ParticipantId ?? p.participantId ?? '');
          side = team && team === homeId ? '1' : (team && team === awayId ? '2' : '');
        }
        const st = {};
        const list = p.Statistics || p.statistics || p.Stats || p.stats || [];
        if (Array.isArray(list)) {
          list.forEach(s => {
            const t = parseInt(s.Type ?? s.type ?? s.Id ?? s.id);
            if (!PLAYER_TYPES.includes(t)) return;
            const v = statValue(s);
            if (v !== null) st[t] = v;
          });
        }
        return {
          id: String(p.PlayerId ?? p.playerId ?? p.Id ?? p.id ?? ''),
          name: p.PlayerName || p.playerName || p.Name || p.name || '',
          side,
          st
        };
      }).filter(p => p.id);
      return {
        score: resOf(sb.Results || sb.results),
        period: parseInt(sb.CurrentPeriod ?? sb.currentPeriod ?? 0) || 0,
        stats,
        extra: readExtra(ls),
        players
      };
    },
    feedKey() { return this.feed ? JSON.stringify(this.feed) : ''; },
    score() { return this.feed ? this.feed.score : { h: 0, a: 0 }; },
    half() { return this.batSide === '2' ? 'top' : 'bottom'; },
    inningText() {
      const p = this.feed ? this.feed.period : 0;
      if (p === 40 && this.view.inning < 10) return 'EX';
      return String(this.view.inning || 1);
    },
    // Runs per inning (extra innings included) for the final badge.
    lineScore() {
      const periods = (this.livescore && (this.livescore.Periods || this.livescore.periods)) || [];
      return periods
        .filter(p => { const t = parseInt(p.Type ?? p.type); return (t >= 1 && t <= 20) || t === 40; })
        .sort((a, b) => parseInt(a.Type ?? a.type) - parseInt(b.Type ?? b.type))
        .map(p => {
          const res = p.Results || p.results || [];
          const val = pos => { const r = res.find(x => String(x.Position ?? x.position) === pos); return r ? (r.Value ?? r.value ?? 0) : 0; };
          return { h: val('1'), a: val('2') };
        });
    },
    // Header numbers: the player when the feed tells us who it is, the
    // team's totals otherwise.
    batterInfo() {
      const side = this.batSide;
      const f = this.feed;
      const extra = f ? f.extra : {};
      const p = this.findPlayer(side, extra.batter) || (this.batterId[side] && this.playerById(this.batterId[side]));
      const fmt = v => (v === null || v === undefined ? 0 : v);
      if (p) {
        return {
          name: p.name,
          stats: [['AB', PS.B_AT_BATS], ['R', PS.B_RUNS], ['H', PS.B_HITS], ['RBI', PS.B_RBI]].map(([label, t]) => ({ label, value: fmt(p.st[t]) }))
        };
      }
      const st = f ? f.stats[side] : {};
      return {
        name: extra.batter && isNaN(Number(extra.batter)) ? extra.batter : '',
        stats: [
          { label: 'AB', value: fmt(st[ST.AT_BATS]) },
          { label: 'R', value: side === '1' ? this.score.h : this.score.a },
          { label: 'H', value: fmt(st[ST.HITS]) },
          { label: 'RBI', value: fmt(st[ST.RBI]) }
        ]
      };
    },
    pitcherInfo() {
      const side = other(this.batSide);
      const f = this.feed;
      const extra = f ? f.extra : {};
      const p = this.findPlayer(side, extra.pitcher) || (this.pitcherId[side] && this.playerById(this.pitcherId[side]));
      const fmt = v => (v === null || v === undefined ? 0 : v);
      if (p) {
        return {
          name: p.name,
          stats: [['IP', PS.P_INNINGS], ['H', PS.P_HITS], ['BB', PS.P_WALKS], ['K', PS.P_STRIKEOUTS]].map(([label, t]) => ({ label, value: fmt(p.st[t]) }))
        };
      }
      const st = f ? f.stats[side] : {};
      const opp = f ? f.stats[this.batSide] : {};
      const first = (...vals) => vals.find(v => v !== null && v !== undefined);
      return {
        name: extra.pitcher && isNaN(Number(extra.pitcher)) ? extra.pitcher : '',
        stats: [
          { label: 'IP', value: fmt(st[ST.INNINGS_PITCHED]) },
          { label: 'H', value: fmt(first(st[ST.HITS_ALLOWED], opp[ST.HITS])) },
          { label: 'BB', value: fmt(first(st[ST.WALKS_ALLOWED], st[ST.WALKS_ALLOWED_RAW], opp[ST.WALKS])) },
          { label: 'K', value: fmt(opp[ST.STRIKEOUTS]) }
        ]
      };
    },
    // Static field geometry (screen points).
    geo() {
      const arc = (inset, a0, a1, n = 40) => {
        const out = [];
        for (let i = 0; i <= n; i++) out.push(fenceAt(a0 + (a1 - a0) * i / n, inset));
        return out;
      };
      const circle = (c, r, n = 32) => {
        const out = [];
        for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; out.push({ x: c.x + Math.cos(a) * r, y: c.y + Math.sin(a) * r }); }
        return out;
      };
      const wall = arc(0, -QUARTER, QUARTER);
      // Wall height in px, a bit taller than true scale so the ad-boards read.
      const wallH = p => 11 + 30 * p.k;
      const cornerL = p3(wall[0].x, wall[0].y);
      const cornerR = p3(wall[wall.length - 1].x, wall[wall.length - 1].y);
      const foulGround = [[0, cornerL.y + 2], ...wall.map(p => { const s = p3(p.x, p.y); return [s.x, s.y]; }), [STAGE_W, cornerR.y + 2], [STAGE_W, STAGE_H], [0, STAGE_H]]
        .map(p => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
      // Ad-boards along the wall (a few straight pieces) and down the lines.
      const boards = [];
      const cuts = [-QUARTER, -QUARTER / 2, 0, QUARTER / 2, QUARTER];
      for (let i = 0; i < cuts.length - 1; i++) {
        const a = fenceAt(cuts[i]);
        const b = fenceAt(cuts[i + 1]);
        const pa = p3(a.x, a.y);
        const pb = p3(b.x, b.y);
        boards.push(makeBoard('w' + i, pa, pb, wallH(pa), wallH(pb), 2, 42));
      }
      boards.push(makeBoard('sl', { x: -6, y: cornerL.y + 4 }, cornerL, 20, wallH(cornerL), 1, 42));
      boards.push(makeBoard('sr', cornerR, { x: STAGE_W + 6, y: cornerR.y + 4 }, wallH(cornerR), 20, 1, 42));
      const fair = poly([PLATE, ...wall.slice().reverse(), PLATE]);
      const stripes = [];
      for (let y = 6, i = 0; y < 130; y += 7, i++) {
        if (i % 2) continue;
        stripes.push(poly([{ x: -140, y }, { x: 140, y }, { x: 140, y: y + 7 }, { x: -140, y: y + 7 }]));
      }
      const track = poly([...arc(0, -QUARTER, QUARTER), ...arc(4.5, QUARTER, -QUARTER)]);
      // Infield dirt: the arc around the mound down to the foul lines.
      const dirtArc = [];
      const aMax = 1.25;
      for (let i = 0; i <= 30; i++) {
        const a = aMax - 2 * aMax * i / 30;
        dirtArc.push({ x: 29 * Math.sin(a), y: MOUND.y + 29 * Math.cos(a) });
      }
      const infieldDirt = poly([{ x: 0, y: 0 }, { x: 27.5, y: 27.5 }, ...dirtArc, { x: -27.5, y: 27.5 }]);
      const g = B - 2.4;
      const infieldGrass = poly([{ x: 0, y: B - g }, { x: g, y: B }, { x: 0, y: B + g }, { x: -g, y: B }]);
      const lines = [
        pathOf([PLATE, wall[wall.length - 1]]),
        pathOf([PLATE, wall[0]]),
        pathOf([{ x: -1.75, y: -0.9 }, { x: -0.45, y: -0.9 }, { x: -0.45, y: 0.9 }, { x: -1.75, y: 0.9 }]) + ' Z',
        pathOf([{ x: 1.75, y: -0.9 }, { x: 0.45, y: -0.9 }, { x: 0.45, y: 0.9 }, { x: 1.75, y: 0.9 }]) + ' Z'
      ];
      const poles = [wall[0], wall[wall.length - 1]].map(w => pathOf([{ ...w, h: 0 }, { ...w, h: 15 }]));
      return {
        foulGround,
        boards,
        fair,
        stripes,
        track,
        infieldDirt,
        infieldGrass,
        homeCircle: poly(circle(PLATE, 4.2)),
        mound: poly(circle(MOUND, 2.7, 24)),
        rubber: poly([{ x: -0.35, y: 18.3 }, { x: 0.35, y: 18.3 }, { x: 0.35, y: 18.5 }, { x: -0.35, y: 18.5 }]),
        plate: poly([{ x: -0.32, y: 0.32 }, { x: 0.32, y: 0.32 }, { x: 0.32, y: 0 }, { x: 0, y: -0.32 }, { x: -0.32, y: 0 }]),
        poles
      };
    },
    baseViews() {
      return [1, 2, 3].map(n => {
        const c = BASES[n];
        const r = 0.6;
        return {
          n,
          c: p3(c.x, c.y),
          on: this.view.bases[n - 1] && !this.movingFrom.includes(n),
          points: poly([{ x: c.x, y: c.y - r }, { x: c.x + r, y: c.y }, { x: c.x, y: c.y + r }, { x: c.x - r, y: c.y }])
        };
      });
    },
    // Bases whose runner is being animated by the current play.
    movingFrom() {
      const ev = this.ev;
      if (!ev || ev.applied) return [];
      return ev.moves.filter(mv => mv.from > 0).map(mv => mv.from);
    },
    figures() {
      const ev = this.ev && !this.ev.applied ? this.ev : null;
      const t = this.evT;
      const idle = this.now / 1000;
      const batCol = this.batColor;
      const fieldCol = this.fieldColor;
      const list = [];
      const add = (key, pos, color, extra = {}) => {
        const s = p3(pos.x, pos.y);
        list.push({ key, x: s.x, y: s.y, k: clamp(s.k, 0.3, 1.2), color, o: 1, bat: null, crouch: false, depth: pos.y, ...extra });
      };
      // Fielders (the one chasing the ball runs to it).
      Object.keys(FIELD_POS).forEach(k => {
        let pos = FIELD_POS[k];
        const ch = ev && ev.plan.chaser;
        if (ch && ch.pos === k && t > ch.start) {
          const u = ease(clamp((t - ch.start) / ch.dur, 0, 1));
          pos = { x: lerp(pos.x, ch.to.x, u), y: lerp(pos.y, ch.to.y, u) };
        } else if (k !== 'C' && k !== 'P') {
          // Idle: fielders shift their weight a little.
          pos = { x: pos.x + Math.sin(idle * 1.3 + pos.x) * 0.35, y: pos.y };
        }
        add('f' + k, pos, fieldCol, { crouch: k === 'C' });
      });
      // Runners standing on their base.
      [1, 2, 3].forEach(n => {
        if (this.view.bases[n - 1] && !this.movingFrom.includes(n)) {
          const b = BASES[n];
          add('r' + n, { x: b.x + (n === 1 ? -0.9 : (n === 3 ? 0.9 : 0)), y: b.y - 0.8 }, batCol);
        }
      });
      // Runners on the move.
      let batterRunning = false;
      if (ev) {
        ev.moves.forEach((mv, i) => {
          if (mv.from === 0 && t < mv.start) return;
          if (mv.from === 0) batterRunning = true;
          const r = this.runnerAt(mv, t);
          add('m' + i + '-' + ev.key, r, batCol, { o: r.o });
        });
      }
      // Batter at the plate: bat waggling, swinging on contact.
      if (!batterRunning && !this.finished) {
        let bat = BAT_IDLE + Math.sin(idle * 2.4) * 7;
        if (ev && ev.plan.swingAt !== null && ev.plan.swingAt !== undefined && t >= ev.plan.swingAt) {
          const u = clamp((t - ev.plan.swingAt) / 220, 0, 1);
          bat = lerp(BAT_IDLE, 165, ease(u));
        }
        add('batter', BATTER_SPOT, batCol, { bat, depth: -0.5 });
      }
      return list.sort((a, b) => b.depth - a.depth);
    },
    ballView() {
      const ev = this.ev;
      if (!ev || !ev.plan) return null;
      const t = this.evT;
      const pl = ev.plan;
      const p = this.segAt(pl.pitch, t) || this.segAt(pl.flight, t) || this.segAt(pl.throw, t);
      if (!p) return null;
      const g = p3(p.x, p.y, 0);
      const up = p3(p.x, p.y, p.h);
      return { x: up.x, y: up.y, sx: g.x, sy: g.y, r: clamp(2 * g.k, 0.8, 2.4) };
    },
    landing() {
      const ev = this.ev;
      if (!ev || !ev.plan.land || !ev.plan.flight) return null;
      const fl = ev.plan.flight;
      if (this.evT < fl.start + fl.dur) return null;
      const s = p3(ev.plan.land.x, ev.plan.land.y);
      return { key: ev.key, x: s.x, y: s.y, rx: 10 * s.k + 3, ry: 3.5 * s.k + 1 };
    },
    fireworks() {
      const ev = this.ev;
      return ev && ev.plan.fireworksAt && this.evT >= ev.plan.fireworksAt ? ev.key : '';
    },
    card() {
      if (!this.cardOn || !this.ev || !this.ev.card || this.finished) return null;
      return { key: this.ev.key, ...this.ev.card };
    }
  }
};
</script>

<style scoped>
.bb-root { position: relative; width: 100%; overflow: hidden; background: #15151c; }
.bb-stage { position: absolute; top: 0; left: 0; width: 460px; height: 290px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; color: #fff; }
.bb-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.bb-attack-glow { animation: bb-glow 2.4s ease-in-out infinite; }
@keyframes bb-glow { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
.bb-base-glow { opacity: 0.55; animation: bb-base 1.6s ease-in-out infinite; }
@keyframes bb-base { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.75; } }
.bb-landing { transform-box: fill-box; transform-origin: 50% 50%; animation: bb-land 0.9s ease-out both; }
@keyframes bb-land { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }
.bb-burst { transform-box: fill-box; transform-origin: 50% 50%; opacity: 0; animation: bb-burst 1.1s ease-out both; }
@keyframes bb-burst { 0% { transform: scale(0.1); opacity: 1; } 70% { opacity: 1; } 100% { transform: scale(1.3); opacity: 0; } }

/* Batter / pitcher panels. */
.bb-header { position: absolute; top: 0; left: 0; right: 0; height: 52px; display: flex; z-index: 2; }
.bb-panel { flex: 1 1 50%; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 6px 10px; box-sizing: border-box; background-color: #1b1b22; }
.bb-panel.is-pitcher { justify-content: flex-end; text-align: right; }
.bb-jersey { width: 20px; height: 20px; flex-shrink: 0; margin-top: 4px; filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5)); }
.bb-panel-body { min-width: 0; }
.bb-panel-label { font-size: var(--font-size-md); color: #e5e7eb; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }
.bb-panel-label b { color: #fff; font-weight: 700; }
.bb-panel-stats { display: flex; gap: 14px; margin-top: 2px; }
.bb-panel.is-pitcher .bb-panel-stats { justify-content: flex-end; }
.bb-stat { display: flex; flex-direction: column; align-items: flex-start; min-width: 18px; line-height: 1.15; }
.bb-stat span { font-size: var(--font-size-xs); color: rgba(255, 255, 255, 0.75); }
.bb-stat b { font-size: var(--font-size-2xl); font-weight: 700; color: #fff; }

/* Inning / bases / count row. */
.bb-status {
  position: absolute; top: 52px; left: 0; right: 0; height: 50px; z-index: 2; display: flex; align-items: center; gap: 12px; padding: 0 14px; box-sizing: border-box;
  background: linear-gradient(180deg, rgba(34, 32, 32, 0.96) 0%, rgba(34, 32, 32, 0.88) 100%); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35);
}
.bb-inning { display: flex; flex-direction: column; align-items: center; gap: 2px; min-width: 16px; }
.bb-inning-no { font-size: var(--font-size-5xl); font-weight: 800; line-height: 1; }
.bb-half { width: 8px; height: 5px; fill: rgba(255, 255, 255, 0.3); }
.bb-half.is-on { fill: #fff; }
.bb-diamond { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.bb-diamond svg { width: 30px; height: 20px; }
.bb-outs { display: flex; gap: 4px; }
.bb-outs span { width: 5px; height: 5px; border-radius: 50%; background: rgba(255, 255, 255, 0.28); }
.bb-outs span.is-on { background: #fff; }
.bb-count { font-size: var(--font-size-5xl); font-weight: 800; white-space: nowrap; min-width: 44px; text-align: center; font-variant-numeric: tabular-nums; }
.bb-sep { width: 1px; height: 30px; background: rgba(255, 255, 255, 0.2); }
.bb-base-col { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.bb-base-label { font-size: var(--font-size-xs); color: rgba(255, 255, 255, 0.6); }
.bb-base-value { font-size: var(--font-size-md); color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; border-left: 2px solid transparent; padding-left: 0; transition: padding 0.3s; }
.bb-base-value.is-on { font-weight: 700; padding-left: 4px; }

/* Play card. */
.bb-overlay { position: absolute; top: 52px; left: 0; right: 0; bottom: 0; z-index: 4; display: flex; align-items: center; justify-content: center; background: rgba(10, 10, 12, 0.5); pointer-events: none; }
.bb-card {
  position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; min-width: 170px; max-width: 300px; padding: 12px 22px;
  background: rgba(118, 108, 102, 0.9); border-radius: 6px; box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5); overflow: hidden;
  animation: bb-card-in 0.45s cubic-bezier(.2, 1.4, .4, 1) both;
}
.bb-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--side-color); }
.bb-card-title { font-size: var(--font-size-10xl); font-weight: 900; font-style: italic; text-transform: uppercase; line-height: 1.05; text-shadow: 0 2px 8px rgba(0, 0, 0, 0.35); white-space: nowrap; animation: bb-title 0.5s cubic-bezier(.2, 1.5, .4, 1) 0.08s both; }
.bb-card-sub { font-size: var(--font-size-md); font-weight: 600; color: rgba(255, 255, 255, 0.9); max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: bb-rise 0.35s ease-out 0.25s both; }
.bb-card-icon { width: 30px; height: 30px; animation: bb-bat 0.6s cubic-bezier(.2, 1.4, .4, 1) both; }
.bb-card-badge {
  min-width: 34px; height: 34px; padding: 0 8px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; border-radius: 17px;
  background: var(--side-color); color: var(--side-text); font-size: var(--font-size-5xl); font-weight: 900; box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.18);
  animation: bb-badge 0.6s cubic-bezier(.2, 1.6, .4, 1) both;
}
.bb-card-score { display: flex; gap: 8px; margin-top: 4px; padding: 2px 12px; background: #0b0b0b; border-radius: 4px; font-size: var(--font-size-5xl); font-weight: 800; animation: bb-rise 0.35s ease-out 0.4s both; }
.bb-card-score .is-won { color: #e9e33b; display: inline-block; animation: bb-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) 0.6s both; }
.bb-card-sep { opacity: 0.6; }

.bb-card.is-pitch { flex-direction: row; align-items: stretch; gap: 0; padding: 10px 0 0 18px; min-width: 200px; }
.bb-card-main { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding-bottom: 8px; }
.bb-zone { width: 58px; height: 62px; margin-left: 8px; }
.bb-zone-dot { transform-box: fill-box; transform-origin: 50% 50%; animation: bb-dot 0.5s cubic-bezier(.2, 1.6, .4, 1) 0.1s both; }
.bb-card-count { font-size: var(--font-size-2xl); font-weight: 800; color: rgba(255, 255, 255, 0.9); margin-left: 8px; }
.bb-batter-art { width: 76px; height: 114px; margin-left: 6px; align-self: flex-end; animation: bb-slide 0.5s cubic-bezier(.2, 1, .3, 1) both; }

@keyframes bb-card-in { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes bb-title { from { transform: scale(1.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes bb-rise { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes bb-badge { from { transform: scale(0.2) rotate(-20deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes bb-pop { 0% { transform: scale(0.4); } 40% { transform: scale(1.5); } 100% { transform: scale(1); } }
@keyframes bb-dot { from { transform: scale(2.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes bb-slide { from { transform: translateX(30px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes bb-bat { from { transform: rotate(-70deg) scale(0.5); opacity: 0; } to { transform: rotate(0) scale(1); opacity: 1; } }
.bb-card-enter-active { transition: opacity 0.2s; }
.bb-card-leave-active { transition: opacity 0.3s; }
.bb-card-enter, .bb-card-leave-to { opacity: 0; }

/* Match over. */
.bb-final { position: absolute; inset: 0; z-index: 6; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.bb-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 28px 16px; max-width: 420px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: bb-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.bb-final-icon { width: 40px; height: 40px; animation: bb-trophy 2s ease-in-out 0.7s infinite; }
.bb-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.bb-final-score { display: flex; gap: 8px; color: #cbd5e1; font-size: var(--font-size-5xl); font-weight: 700; white-space: nowrap; }
.bb-final-score .is-win { color: #fff; }
.bb-final-innings { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-top: 2px; color: #94a3b8; font-size: var(--font-size-2xl); font-weight: 700; }
@keyframes bb-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes bb-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.bb-final-enter-active { transition: opacity 0.3s; }
.bb-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
